import { ApiResponse } from '@/domain';
import { CreateBillingPeriodDto, ModifyBillingPeriodDto } from '@/domain/subscriptions/subscriptions';
import { subscriptionsApi } from '@/rest/modules/subscriptions-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../use-toast';
import { useNavigate } from '@tanstack/react-router';
import { Paths } from '@/routes/paths';

export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  lists: () => [...subscriptionKeys.all, 'list'] as const,
  subscriptions: (contractId: string) => [...subscriptionKeys.lists(), 'subscriptions', { contractId }] as const,
  installments: (contractId: string) => [...subscriptionKeys.lists(), 'installments', { contractId }] as const,
};

export const useSubscriptions = (contractId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: subscriptionKeys.subscriptions(contractId),
    queryFn: () => subscriptionsApi.getSubscriptions(contractId),
    enabled: (options?.enabled ?? true) && !!contractId,
  });
};

export const useInstallments = (contractId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: subscriptionKeys.installments(contractId),
    queryFn: () => subscriptionsApi.getInstallments(contractId),
    enabled: (options?.enabled ?? true) && !!contractId,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useCreateBillingPeriodPayment = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ contractId, billingPeriodId }: { contractId: string, billingPeriodId: string }) =>
      subscriptionsApi.createBillingPeriodPayment(contractId, billingPeriodId),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['agreement-payments', variables.contractId] });
      response.data.payment.id && navigate({ to: `${Paths.Management.SubscriptionPeriodPayments}/${variables.billingPeriodId}/${Paths.Management.Payments}/${response.data.payment.id}` })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al crear el período',
        variant: 'destructive',
      })
    },
  })
}

export const useCreateNewBillingPeriod = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: { subscriptionId: string } & CreateBillingPeriodDto) =>
      subscriptionsApi.createBillingPeriod(data.subscriptionId, data),
    onSuccess: (response, variables) => {
      toast({
        title: "Éxito",
        description: "Nuevo período creado",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      return response;
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al crear el período',
        variant: 'destructive',
      })
    },
  })
}

export const useCreateMultiSubscriptionPayment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ subscriptionId, billingPeriodIds }: {
      subscriptionId: string;
      billingPeriodIds: string[];
    }) =>
      subscriptionsApi.createMultiSubscriptionPayment(subscriptionId, { billingPeriodIds }),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.subscriptions(variables.subscriptionId) });
      return response
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al crear el pago múltiple',
        variant: 'destructive',
      })
    },
  })
}

export const useCreateCustomSubscriptionPayment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ subscriptionId, billingPeriodIds, installments, installmentAmount }: {
      subscriptionId: string;
      billingPeriodIds: string[];
      installments: number;
      installmentAmount: number;
    }) =>
      subscriptionsApi.createCustomSubscriptionPayment(subscriptionId, { billingPeriodIds, installments, installmentAmount }),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.subscriptions(variables.subscriptionId) });
      toast({
        title: 'Éxito',
        description: 'Cuotas armadas exitosamente',
        variant: 'success',
      });
      return response
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al armar cuotas',
        variant: 'destructive',
      })
    },
  })
}

export const useDeleteBillingPeriod = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ subscriptionId, billingPeriodId }: { subscriptionId: string; billingPeriodId: string }) =>
      subscriptionsApi.deleteBillingPeriod(subscriptionId, billingPeriodId),
    onSuccess: (_, { subscriptionId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.subscriptions(subscriptionId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.installments(subscriptionId) });
      queryClient.invalidateQueries({ queryKey: ['agreement-payments', subscriptionId] });
      toast({
        title: 'Éxito',
        description: 'Período eliminado exitosamente',
        variant: 'default',
      });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar el período',
        variant: 'destructive',
      });
    },
  });
};

export const useCreateNextInstallment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ contractId, planId }: { contractId: string; planId: string }) =>
      subscriptionsApi.generateInstallment(contractId, planId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.installments(variables.contractId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.subscriptions(variables.contractId) });
      queryClient.invalidateQueries({ queryKey: ['agreement-payments', variables.contractId] });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      toast({
        title: 'Éxito',
        description: 'Cuota generada exitosamente',
        variant: 'success',
      });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al generar la cuota',
        variant: 'destructive',
      });
    },
  });
};

export const useModifyBillingPeriod = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ subscriptionId, billingPeriodId, data }: {
      subscriptionId: string;
      billingPeriodId: string;
      data: ModifyBillingPeriodDto;
    }) =>
      subscriptionsApi.modifyBillingPeriod(subscriptionId, billingPeriodId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.subscriptions(variables.subscriptionId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.installments(variables.subscriptionId) });
      queryClient.invalidateQueries({ queryKey: ['agreement-payments', variables.subscriptionId] });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      toast({
        title: 'Éxito',
        description: 'Período modificado exitosamente',
        variant: 'success',
      });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al modificar el período',
        variant: 'destructive',
      });
    },
  });
};
